import { endpoints } from '@/api/endpoints';
import type { ServerResponse } from '@/api/types';
import {
  activateCampaignResponseSchema,
  emailVerificationResponseSchema,
  getCampaignResponseSchema,
  getUserIdProtectResponseSchema,
  getUserPaymentProtectionInsuranceResponseSchema,
  getUserTerminateAccountDetailsResponseSchema,
  getUserTransactionsResponseSchema,
  mobileVerificationResponseSchema,
} from '@/api/user/schemas';
import type {
  GetUserTransactionsResponse,
  TransactionDetail,
  TransactionDetailType,
  TransactionItem,
  TransactionItemsProduct,
  TransactionStatus,
  TransactionSuffix,
  TransactionType,
} from '@/api/user/types';
import { apiRequest } from '@/api/utils/api';
import type { CampaignName } from '@/constants/SE';
import type { ProfileFormModel } from '@/types/user';
import { reportException } from '@/utils/error';

export const updateUserDetails = async (userFields: ProfileFormModel) => {
  const result = await apiRequest<ServerResponse<ProfileFormModel>>({
    url: endpoints.user.update,
    method: 'PATCH',
    data: userFields,
  });
  return result.data;
};

export const getTransactionSuffix = (transaction: GetUserTransactionsResponse[number]) => {
  if (!['DEPOSIT', 'WITHDRAW'].includes(transaction.type)) {
    return null;
  }

  let suffix: NonNullable<TransactionSuffix> | undefined;

  for (const item of transaction.items) {
    if (
      item.product === 'CREDIT' ||
      item.product === 'DEBT_CONSOLIDATION' ||
      item.product === 'LOAN' ||
      item.product === 'LOAN_ID_PROTECT'
    ) {
      suffix = item.product;
      break;
    }
  }

  if (!suffix) {
    reportException(new Error('No suffix found for transaction item', { cause: transaction }), {
      level: 'error',
      extra: { transaction },
    });
    return null;
  }

  return suffix;
};

const transactionDetailTypeOrder: TransactionDetailType[] = [
  'INTEREST',
  'PRINCIPAL',
  'WITHDRAWAL_FEE',
  'DIRECT_DEBIT_MISSING_FEE',
  'PREMIUM',
  'VAT',
];

const compareBreakdownDetails = (a: TransactionDetail, b: TransactionDetail) =>
  transactionDetailTypeOrder.indexOf(a.type) - transactionDetailTypeOrder.indexOf(b.type);

const productOrder: TransactionItemsProduct[] = [
  'CREDIT',
  'DEBT_CONSOLIDATION',
  'PAYMENT_PROTECTION_INSURANCE',
  'ID_PROTECT',
];

const compareProductOrder = (a: TransactionItem, b: TransactionItem) =>
  productOrder.indexOf(a.product) - productOrder.indexOf(b.product);

export const transformTransactions = (transactions: GetUserTransactionsResponse) =>
  transactions.map(transaction => ({
    ...transaction,
    items: transaction.items.toSorted(compareProductOrder).map(item => ({
      ...item,
      details: item.details.toSorted(compareBreakdownDetails),
    })),
    suffix: getTransactionSuffix(transaction),
  }));

type GetUserTransactionsOptions = {
  type?: TransactionType[];
  status?: TransactionStatus[];
  limit?: number;
  order?: 'ASC' | 'DESC';
};

export const getUserTransactions = async (options: GetUserTransactionsOptions = {}) => {
  const transactions = await apiRequest({
    url: '/user/transactions',
    method: 'GET',
    schema: getUserTransactionsResponseSchema,
    params: options,
  });
  return transformTransactions(transactions);
};

export const getUserIdProtect = () =>
  apiRequest({
    url: endpoints.user.addonProducts.idProtect,
    method: 'GET',
    schema: getUserIdProtectResponseSchema,
  });

export const activateUserIdProtect = () =>
  apiRequest<void>({ url: endpoints.user.addonProducts.idProtect, method: 'POST' });

export const terminateUserIdProtect = () =>
  apiRequest<void>({ url: endpoints.user.addonProducts.idProtect, method: 'DELETE' });

export const getUserPaymentProtectionInsurance = () =>
  apiRequest({
    url: endpoints.user.addonProducts.paymentProtectionInsurance,
    method: 'GET',
    schema: getUserPaymentProtectionInsuranceResponseSchema,
  });

export const activatePaymentProtectionInsurance = () =>
  apiRequest<void>({ url: endpoints.user.addonProducts.paymentProtectionInsurance, method: 'POST' });

export const terminatePaymentProtectionInsurance = () =>
  apiRequest<void>({ url: endpoints.user.addonProducts.paymentProtectionInsurance, method: 'DELETE' });

type TerminateAccountPayload = {
  terminationReason:
    'NO_LONGER_NEEDED' | 'TOO_EXPENSIVE' | 'SWITCHED_TO_COMPETITOR' | 'MISSING_FEATURES' | 'OTHER_REASON';
  comment?: string;
};

export const terminateAccount = ({ terminationReason, comment }: TerminateAccountPayload) =>
  apiRequest<void>({
    url: endpoints.user.terminateAccount,
    method: 'POST',
    data: { terminationReason, comment },
  });

export const getUserTerminateAccountDetails = () =>
  apiRequest({
    url: endpoints.user.terminateAccountDetails,
    method: 'GET',
    schema: getUserTerminateAccountDetailsResponseSchema,
  });

export const getCampaign = (name: CampaignName) =>
  apiRequest({
    url: endpoints.user.campaigns.campaign(name),
    method: 'GET',
    schema: getCampaignResponseSchema,
  });

export const activateCampaign = (name: CampaignName) =>
  apiRequest({
    url: endpoints.user.campaigns.activate(name),
    method: 'POST',
    schema: activateCampaignResponseSchema,
  });

export const declineCampaign = (name: CampaignName) =>
  apiRequest<void>({ url: endpoints.user.campaigns.decline(name), method: 'POST' });

export const markCampaignAsViewed = (name: CampaignName) =>
  apiRequest<void>({ url: endpoints.user.campaigns.viewed(name), method: 'POST' });

export const sendEmailVerificationCode = () =>
  apiRequest({
    url: endpoints.user.emailVerification.send,
    method: 'POST',
    schema: emailVerificationResponseSchema,
  });

export const verifyEmailVerificationCode = (code: string) =>
  apiRequest<void>({
    url: endpoints.user.emailVerification.verify,
    method: 'POST',
    data: { code },
  });

export const updateEmail = (email: string) =>
  apiRequest({
    url: endpoints.user.email,
    method: 'PATCH',
    data: { email },
    schema: emailVerificationResponseSchema,
  });

export const sendMobileVerificationCode = () =>
  apiRequest({
    url: endpoints.user.mobileVerification.send,
    method: 'POST',
    schema: mobileVerificationResponseSchema,
  });

export const updateMobile = (mobile: string) =>
  apiRequest({
    url: endpoints.user.mobile,
    method: 'PATCH',
    data: { mobile },
    schema: mobileVerificationResponseSchema,
  });

export const verifyMobileVerificationCode = (code: string) =>
  apiRequest<void>({
    url: endpoints.user.mobileVerification.verify,
    method: 'POST',
    data: { code },
  });
